/**
 * Importer for vocabulary CSV / JSON files (BW18 PR-J, multilingual via
 * HOTFIX-BW19.4 PR-A Bug 8).
 *
 * Each record is one source word with N translations (one per target
 * language). The importer accepts:
 *
 * Multi-column CSV:
 *   source_text,source_lang,tags,example_sentence,phonetic_ipa,notes,en,de,it,es
 *   chien,fr,animals,Le chien aboie.,ʃjɛ̃,,dog,Hund,cane,perro
 *
 * JSON shorthand:
 *   [{ "source_text": "chien", "source_lang": "fr", "tags": ["animals"],
 *      "translations": { "en": "dog", "de": "Hund" } }]
 *
 * JSON with per-translation overrides:
 *   { "translations": { "en": { "text": "dog", "phonetic_ipa": "dɒɡ" } } }
 *
 * Legacy single-target CSV (source_text + target_text columns) still
 * parses; it lands as a one-translation record.
 *
 * Returns the records ready to insert; the caller adds user_id and
 * fans rows out into vocab_card + vocab_translation.
 */

export type TranslationInput = {
  target_lang: string;
  target_text: string;
  example_sentence?: string | null;
  phonetic_ipa?: string | null;
  notes?: string | null;
};

export type ImportRecord = {
  source_lang: string;
  source_text: string;
  example_sentence?: string | null;
  phonetic_ipa?: string | null;
  notes?: string | null;
  tags: string[];
  translations: TranslationInput[];
};

const KNOWN_LANGS = new Set([
  "en",
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "nl",
  "sv",
  "no",
  "fi",
  "da",
  "pl",
  "cs",
  "ja",
  "zh",
  "ko",
  "ru",
  "tr",
  "ar",
]);

const RESERVED_KEYS = new Set([
  "source_text",
  "source_lang",
  "example_sentence",
  "phonetic_ipa",
  "notes",
  "tags",
  "front",
  "term",
  "translations",
  "back",
  "translation",
  "target_text",
  "target_lang",
]);

export function parseImport(
  raw: string,
  defaultSourceLang = "fr",
  defaultTargetLang = "en",
): { records: ImportRecord[]; errors: string[] } {
  const trimmed = raw.trim();
  if (!trimmed) return { records: [], errors: ["File is empty"] };
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    return parseJson(trimmed, defaultSourceLang, defaultTargetLang);
  }
  return parseCsv(trimmed, defaultSourceLang, defaultTargetLang);
}

function parseJson(
  raw: string,
  defaultSourceLang: string,
  defaultTargetLang: string,
): { records: ImportRecord[]; errors: string[] } {
  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch (e) {
    return { records: [], errors: [`Could not parse JSON: ${(e as Error).message}`] };
  }
  const arr = Array.isArray(payload) ? payload : [payload];
  const errors: string[] = [];
  const records: ImportRecord[] = [];
  arr.forEach((entry, idx) => {
    if (typeof entry !== "object" || entry == null) {
      errors.push(`Row ${idx + 1}: not an object`);
      return;
    }
    const result = coerceJson(
      entry as Record<string, unknown>,
      defaultSourceLang,
      defaultTargetLang,
    );
    if (!result.record) {
      errors.push(`Row ${idx + 1}: ${result.reason}`);
      return;
    }
    records.push(result.record);
  });
  return { records, errors };
}

function parseCsv(
  raw: string,
  defaultSourceLang: string,
  defaultTargetLang: string,
): { records: ImportRecord[]; errors: string[] } {
  const lines = raw.split(/\r?\n/).filter((l) => l.length > 0);
  const errors: string[] = [];
  if (lines.length < 2) {
    return { records: [], errors: ["CSV must have a header and at least one row"] };
  }
  const header = splitCsv(lines[0]).map((h) => h.toLowerCase().trim());
  const records: ImportRecord[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsv(lines[i]);
    const obj: Record<string, string> = {};
    header.forEach((key, idx) => {
      obj[key] = (cells[idx] ?? "").trim();
    });
    const result = coerceCsv(
      obj,
      header,
      defaultSourceLang,
      defaultTargetLang,
    );
    if (!result.record) {
      errors.push(`Row ${i + 1}: ${result.reason}`);
      continue;
    }
    records.push(result.record);
  }
  return { records, errors };
}

type CoerceResult = { record: ImportRecord | null; reason: string };

function coerceCsv(
  raw: Record<string, string>,
  header: string[],
  defaultSourceLang: string,
  defaultTargetLang: string,
): CoerceResult {
  const get = (key: string): string => (raw[key] ?? "").trim();

  const source_text = get("source_text") || get("front") || get("term");
  if (!source_text) return { record: null, reason: "missing source_text" };
  const source_lang = get("source_lang") || defaultSourceLang;

  const tags = parseTags(raw.tags);
  const example_sentence = get("example_sentence") || get("example") || null;
  const phonetic_ipa = get("phonetic_ipa") || get("ipa") || null;
  const notes = get("notes") || null;

  // Build translations from any header column that names a known
  // language code (en, de, fr, ...) AND from the legacy
  // target_text+target_lang pair.
  const translations: TranslationInput[] = [];
  for (const col of header) {
    if (RESERVED_KEYS.has(col)) continue;
    if (!KNOWN_LANGS.has(col)) continue;
    const text = get(col);
    if (!text) continue;
    translations.push({ target_lang: col, target_text: text });
  }
  const legacyTarget = get("target_text") || get("back") || get("translation");
  const legacyLang = get("target_lang") || defaultTargetLang;
  if (legacyTarget && !translations.some((t) => t.target_lang === legacyLang)) {
    translations.push({ target_lang: legacyLang, target_text: legacyTarget });
  }
  if (translations.length === 0) {
    return { record: null, reason: "no translations supplied" };
  }
  return {
    record: {
      source_lang,
      source_text,
      example_sentence,
      phonetic_ipa,
      notes,
      tags,
      translations,
    },
    reason: "",
  };
}

function coerceJson(
  raw: Record<string, unknown>,
  defaultSourceLang: string,
  defaultTargetLang: string,
): CoerceResult {
  const get = (key: string): string => {
    const val = raw[key];
    return typeof val === "string" ? val.trim() : "";
  };

  const source_text = get("source_text") || get("front") || get("term");
  if (!source_text) return { record: null, reason: "missing source_text" };
  const source_lang = get("source_lang") || defaultSourceLang;
  const tags = parseTags(raw.tags);
  const example_sentence = get("example_sentence") || get("example") || null;
  const phonetic_ipa = get("phonetic_ipa") || get("ipa") || null;
  const notes = get("notes") || null;

  const translations: TranslationInput[] = [];
  const tx = raw.translations;
  if (tx && typeof tx === "object" && !Array.isArray(tx)) {
    for (const [lang, value] of Object.entries(tx)) {
      const lc = lang.toLowerCase();
      if (typeof value === "string") {
        if (value.trim()) translations.push({ target_lang: lc, target_text: value.trim() });
      } else if (value && typeof value === "object") {
        const v = value as Record<string, unknown>;
        const text = typeof v.text === "string" ? v.text.trim() : "";
        if (!text) continue;
        translations.push({
          target_lang: lc,
          target_text: text,
          example_sentence:
            typeof v.example_sentence === "string" ? v.example_sentence : null,
          phonetic_ipa:
            typeof v.phonetic_ipa === "string" ? v.phonetic_ipa : null,
          notes: typeof v.notes === "string" ? v.notes : null,
        });
      }
    }
  }
  // Top-level lang-keyed shorthand for JSON: { en: "dog", de: "Hund" }
  for (const [k, v] of Object.entries(raw)) {
    const lc = k.toLowerCase();
    if (RESERVED_KEYS.has(lc)) continue;
    if (!KNOWN_LANGS.has(lc)) continue;
    if (typeof v !== "string" || !v.trim()) continue;
    if (translations.some((t) => t.target_lang === lc)) continue;
    translations.push({ target_lang: lc, target_text: v.trim() });
  }
  // Legacy target_text + target_lang single pair.
  const legacyTarget = get("target_text") || get("back") || get("translation");
  const legacyLang = get("target_lang") || defaultTargetLang;
  if (legacyTarget && !translations.some((t) => t.target_lang === legacyLang)) {
    translations.push({ target_lang: legacyLang, target_text: legacyTarget });
  }
  if (translations.length === 0) {
    return { record: null, reason: "no translations supplied" };
  }
  return {
    record: {
      source_lang,
      source_text,
      example_sentence,
      phonetic_ipa,
      notes,
      tags,
      translations,
    },
    reason: "",
  };
}

function parseTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(/[|,]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Minimal RFC-4180-ish CSV row splitter. Handles double-quoted cells
 * and tab-separated input (common Quizlet export pattern).
 */
function splitCsv(line: string): string[] {
  if (line.includes("\t") && !line.includes(",")) {
    return line.split("\t");
  }
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}
